import { tr } from '../../i18n/text';
import { useTranslation } from 'react-i18next';
import { createContext, useCallback, useContext, useId, useMemo, useState } from 'react';

const noop = () => {};
const NumericValidationContext = createContext({
  hasInvalidNumbers: false, registerInvalid: noop, removeInvalid: noop,
  resetNumericDrafts: noop, resetRevision: 0,
});

export const NumericValidationProvider = ({ children }) => {
  useTranslation('app');
  const [invalidFields, setInvalidFields] = useState({});
  const [resetRevision, setResetRevision] = useState(0);
  const feedbackId = useId();
  const registerInvalid = useCallback((id, field) => {
    setInvalidFields((current) => {
      if (current[id]?.error === field.error && current[id]?.label === field.label) return current;
      return { ...current, [id]: field };
    });
  }, []);
  const removeInvalid = useCallback((id) => {
    setInvalidFields((current) => {
      if (!current[id]) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  }, []);
  const resetNumericDrafts = useCallback(() => {
    setInvalidFields({});
    setResetRevision((current) => current + 1);
  }, []);
  const focusFirstInvalid = useCallback(() => {
    const field = Object.values(invalidFields)[0];
    const input = document.getElementById(field?.id);
    if (!input) return;
    field.onReveal?.();
    let ancestor = input.parentElement;
    while (ancestor) {
      if (ancestor.tagName === 'DETAILS') ancestor.open = true;
      ancestor = ancestor.parentElement;
    }
    const focusInput = () => {
      input.focus();
      input.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    };
    if (field.onReveal) requestAnimationFrame(focusInput);
    else focusInput();
  }, [invalidFields]);
  const value = useMemo(() => ({
    hasInvalidNumbers: Object.keys(invalidFields).length > 0,
    invalidLabels: [...new Set(Object.values(invalidFields).map((field) => field.label))],
    feedbackId, registerInvalid, removeInvalid, resetNumericDrafts, resetRevision, focusFirstInvalid,
  }), [invalidFields, feedbackId, registerInvalid, removeInvalid, resetNumericDrafts, resetRevision, focusFirstInvalid]);
  return <NumericValidationContext.Provider value={value}>{children}</NumericValidationContext.Provider>;
};

export const useNumericValidation = () => useContext(NumericValidationContext);

export const NumericValidationFeedback = () => {
  useTranslation('app');
  const { hasInvalidNumbers, feedbackId, invalidLabels } = useNumericValidation();
  if (!hasInvalidNumbers) return null;
  return (
    <div className="sr-only" id={feedbackId}>
      <p role="status">{tr("Há campos numéricos a corrigir. Os resultados exibidos mantêm os últimos valores válidos.")}</p>
      <p>{tr("Verifique:")} {invalidLabels.join('; ')}.</p>
    </div>
  );
};

